import type { SignalConfig } from "@/features/signals/types";
import type { LatestPort, ProductionHistoryWindow } from "@/features/devices/types";
import type { GhmSignalKind } from "@ghm/mqtt-contract";
import {
  historicalProductionWindow,
  podeGerarGrafico as fonteAceitaGrafico,
  resolveChartConfig,
} from "../chartWidgets";
import { widgetDefinesSignalIdentity } from "@/features/signals/signalConfigFromWidget";
import { resolveSignalName } from "@/features/signals/signalName";
import { resolveWidgetForSignalConfig } from "../resolveWidget";
import { StateValueWidget } from "./widgets/StateValueWidget";
import type { DashboardWidget, ResolvedWidgetValue } from "../types";
import type { WidgetAvailabilityState } from "../useAvailability";
import type { WidgetHistoryState } from "../useProductionHistories";
import type { WidgetProductionTrackerState } from "../useProductionTrackers";
import {
  AvailabilityWidget,
  BarWidget,
  ChartWidget,
  GaugeWidget,
  LedWidget,
  MultiSeriesChartWidget,
  ModbusHealthWidget,
  MultiValueWidget,
  ProductionHeroWidget,
  ProductionKpiWidget,
  ProductionStatusWidget,
  RateHeroWidget,
  TankWidget,
  UtilizationWidget,
  ValueHeroWidget,
} from "./widgets";

interface SignalCardProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  /**
   * Valores de TODAS as fontes, âncora primeiro. Só os widgets multi-fonte
   * consomem; os demais seguem usando `value`, que continua sendo a âncora.
   * Ausente = comportamento de fonte única.
   */
  values?: ResolvedWidgetValue[];
  history?: WidgetHistoryState;
  /** Histórico por fonte, na mesma ordem de `values`. */
  histories?: Array<WidgetHistoryState | undefined>;
  /** Rótulo curto por fonte, para a legenda dos widgets comparativos. */
  sourceLabels?: Record<string, string>;
  /** Semantica publicada no meta para curar widgets persistidos antes do campo. */
  sourceKind?: GhmSignalKind;
  /** Nome publicado no meta para esta fonte. Alimenta `resolveSignalName`. */
  sourceName?: string;
  /** Unidade física publicada no meta, separada da unidade da visão derivada. */
  sourceUnit?: string | null;
  /** Unidade do v de taxa da mesma porta, comprovada no meta atual; nunca inferida do acc. */
  sourceRateUnit?: string;
  /**
   * Portas Modbus do MESMO slot da âncora. Só o card de saúde consome — é o
   * único widget de DEVICE, e a pergunta que ele responde ("o barramento está
   * bem?") não pertence a nenhum ponto individual.
   */
  modbusPorts?: LatestPort[];
  availability?: WidgetAvailabilityState;
  /**
   * A porta ancora declara `_v` no `/meta` atual? Disponibilidade historica
   * mede MOVIMENTO; sem essa grandeza nao ha o que medir, e o card precisa
   * dizer isso em vez de exibir o zero que o backend forma por falta de dado.
   * `undefined` = ainda nao se sabe (meta nao chegou).
   */
  movimentoDeclarado?: boolean;
  productionTracker?: WidgetProductionTrackerState;
  onResetProduction?: () => Promise<void>;
  /** Quando definido, sobrescreve título/widgetType/settings com a configuração persistida do operador. */
  signalConfig?: SignalConfig;
  editable?: boolean;
  onConfigure?: (widget: DashboardWidget) => void;
  /**
   * Gera um gráfico a partir deste widget (menu `…` em Visualização). Só é
   * acionável para sinais numéricos (`v`/`acc`); o card decide o gating.
   */
  onGenerateChart?: (widget: DashboardWidget) => void;
  /** Desfaz o "Gerar grafico" e devolve o card ao tipo de origem. */
  onHideChart?: (widget: DashboardWidget) => void;
  /**
   * Troca a janela histórica direto no card (seletor 24h/48h/7d/30d). O pai
   * persiste via `applyWidgetWindow` + upsert. Só os widgets com janela
   * (heróis de tendência/produção/disponibilidade) exibem o seletor.
   */
  onChangeWindow?: (widget: DashboardWidget, window: ProductionHistoryWindow) => void;
}

/**
 * Decide qual widget visual renderizar para um sinal real (`G{n}_P{n}_{s|acc|v}`)
 * ou derivado. Quando o snapshot é indisponível e o widget é numérico,
 * o `ValueHeroWidget` mostra "--" com o estado de ausência (aguardando /
 * sem leitura recente / offline) em sentence case.
 */
export function SignalCard({
  widget,
  value,
  values,
  history,
  histories,
  sourceLabels,
  sourceKind,
  sourceName,
  sourceUnit,
  sourceRateUnit,
  modbusPorts,
  availability,
  movimentoDeclarado,
  productionTracker,
  onResetProduction,
  signalConfig,
  editable = false,
  onConfigure,
  onGenerateChart,
  onHideChart,
  onChangeWindow,
}: SignalCardProps) {
  // O TIPO do widget é sempre o que está salvo nele (catálogo do starter ou
  // escolha do operador no editor). O `signalConfig.preferredWidget` é apenas
  // o widget DEFAULT do sinal e NÃO pode sobrescrever um widget já
  // materializado — senão um `number` de acc vira `production-kpi` ("ritmo
  // atual") e um gráfico vira o KPI padrão da porta. O signalConfig segue
  // valendo para label/unidade/escala/decimais (preferências de exibição).
  // Aplica o SignalConfig do sinal físico (rótulo/unidade/escala) sobre o
  // widget. A lógica de "quem herda o quê" vive em `resolveWidgetForSignalConfig`
  // (pura e testada). Visões derivadas (disponibilidade/utilização) não herdam
  // nada, então o nome amigável editado aparece — antes o card mostrava o
  // `signalConfig.label` antigo enquanto o preview do editor já mostrava o novo.
  const resolvedFromConfig = resolveWidgetForSignalConfig(widget, signalConfig);
  // Preferencia ao `/meta` VIVO, e nao ao que ficou salvo.
  //
  // `settings.signalKind` nao e uma escolha do operador (nao ha campo para
  // ela): e uma COPIA da declaracao do hardware, feita no momento em que o
  // card foi salvo. Deixar a copia vencer tornava a declaracao incorrigivel —
  // redeclarar o `kind` no configurador nao alcancava nenhum dashboard ja
  // montado, e o operador teria que reabrir e salvar card por card.
  //
  // A ordem e: o que o device declara AGORA > o que o `SignalConfig` guardou >
  // o que o widget guardou. Os dois ultimos so entram quando o device esta
  // mudo ou a fonte saiu do `/meta` — ai a copia velha e melhor que nada.
  const effectiveSignalKind =
    sourceKind ?? signalConfig?.kind ?? resolvedFromConfig.settings.signalKind;
  const resolvedWidget =
    effectiveSignalKind && effectiveSignalKind !== resolvedFromConfig.settings.signalKind
      ? {
          ...resolvedFromConfig,
          settings: { ...resolvedFromConfig.settings, signalKind: effectiveSignalKind },
        }
      : resolvedFromConfig;

  // Titulo: escolha do operador > nome declarado pelo dispositivo > o que
  // estiver salvo. So vale para cards cujo titulo E o nome do sinal — um
  // grafico ou um comparativo tem titulo de VISAO ("Historico · Tensao"), e
  // trocar isso pelo nome da fonte ancora seria mentir sobre o que ele mostra.
  // A regra e a mesma invariante de `resolveWidgetForSignalConfig`: herda a
  // identidade quem a define.
  //
  // O nome escolhido tem que vir da MESMA fonte que provou a escolha. Ler
  // sempre `resolvedWidget.title` estava errado: esse titulo ja passou por
  // `resolveWidgetForSignalConfig` e pode ter sido substituido pelo label da
  // config compartilhada. Um card com `renamedByOperator` cuja config nao
  // fosse do operador exibia o label da config no lugar do nome digitado.
  const chosenName = widget.settings.renamedByOperator
    ? widget.title
    : signalConfig?.labelFromOperator
      ? signalConfig.label
      : undefined;
  const namedWidget = widgetDefinesSignalIdentity(resolvedWidget)
    ? {
        ...resolvedWidget,
        title: resolveSignalName(resolvedWidget.source, {
          chosen: chosenName,
          declared: sourceName,
          saved: resolvedWidget.title,
          kind: effectiveSignalKind,
        }),
      }
    : resolvedWidget;

  const handleConfigure = onConfigure ? () => onConfigure(namedWidget) : undefined;
  const handleChangeWindow = onChangeWindow
    ? (window: ProductionHistoryWindow) => onChangeWindow(namedWidget, window)
    : undefined;
  // "Gerar gráfico" só faz sentido para sinais numéricos (v/acc) e não para
  // os próprios gráficos. O WidgetShell só mostra o menu em Visualização.
  // DISPONIBILIDADE e a excecao a `isChartableSource`: o grafico dela e a
  // linha do tempo da disponibilidade calculada, nao a serie do sinal — entao
  // funciona mesmo ancorada num `s`, que nao e plotavel.
  // Quem autoriza e a MATRIZ, nao o sufixo da tag — ver `podeGerarGrafico`.
  const podeGerarGrafico = fonteAceitaGrafico(namedWidget, effectiveSignalKind);
  const handleGenerateChart =
    onGenerateChart && podeGerarGrafico ? () => onGenerateChart(namedWidget) : undefined;
  // Só aparece em card que carrega a marca de origem — ou seja, que realmente
  // nasceu de uma substituição e tem para onde voltar.
  const handleHideChart =
    onHideChart && namedWidget.settings.chartOrigin
      ? () => onHideChart(namedWidget)
      : undefined;

  // Widgets comparativos: precisam do CONJUNTO de fontes. Quando o pai não
  // passa `values` (preview do editor, chamadas antigas), cai no array de um
  // elemento com a âncora — o card degrada para uma leitura só em vez de
  // quebrar.
  const resolvedValues = values && values.length > 0 ? values : [value];

  if (namedWidget.widgetType === "state-value") {
    return <StateValueWidget widget={namedWidget} value={value} editable={editable} onConfigure={handleConfigure} />;
  }

  if (namedWidget.widgetType === "multi-value") {
    return (
      <MultiValueWidget
        widget={namedWidget}
        values={resolvedValues}
        labels={sourceLabels}
        editable={editable}
        onConfigure={handleConfigure}
        onGenerateChart={handleGenerateChart}
      />
    );
  }

  if (namedWidget.widgetType === "multi-chart") {
    return (
      <MultiSeriesChartWidget
        widget={namedWidget}
        values={resolvedValues}
        histories={histories ?? [history]}
        labels={sourceLabels}
        editable={editable}
        onConfigure={handleConfigure}
        onHideChart={handleHideChart}
        onChangeWindow={handleChangeWindow}
      />
    );
  }

  if (namedWidget.widgetType === "modbus-health") {
    return (
      <ModbusHealthWidget
        widget={namedWidget}
        value={value}
        modbusPorts={modbusPorts ?? []}
        editable={editable}
        onConfigure={handleConfigure}
      />
    );
  }

  if (namedWidget.widgetType === "tank") {
    return (
      <TankWidget
        widget={namedWidget}
        value={value}
        editable={editable}
        onConfigure={handleConfigure}
        onGenerateChart={handleGenerateChart}
      />
    );
  }

  if (namedWidget.widgetType === "availability") {
    // SignalConfig não registra autoria da unidade (labelFromOperator prova
    // somente o nome): a cópia salva não pode vencer o meta atual. Sem meta,
    // serve como reserva apenas para a mesma fonte/máquina. settings.unit é
    // da visão derivada e nunca participa desta resolução.
    const savedSourceUnit = signalConfig?.source === widget.source &&
      signalConfig.machineId === widget.machineId ? signalConfig.unit?.trim() : undefined;
    const activitySourceUnit = sourceUnit?.trim() || savedSourceUnit;
    return (
      <AvailabilityWidget
        widget={namedWidget}
        value={value}
        sourceUnit={activitySourceUnit}
        editable={editable}
        onConfigure={handleConfigure}
        onGenerateChart={handleGenerateChart}
      />
    );
  }

  if (namedWidget.widgetType === "utilization") {
    return (
      <UtilizationWidget
        widget={namedWidget}
        value={value}
        availability={availability}
        movimentoDeclarado={movimentoDeclarado}
        editable={editable}
        onConfigure={handleConfigure}
        onHideChart={handleHideChart}
        onChangeWindow={handleChangeWindow}
      />
    );
  }

  if (namedWidget.widgetType === "production-status") {
    return (
      <ProductionStatusWidget
        widget={namedWidget}
        value={value}
        editable={editable}
        onConfigure={handleConfigure}
      />
    );
  }

  // Cartão de referência da nova linguagem (leveza + gráfico como face): a
  // variante de produção por JANELA HISTÓRICA (ex.: "produzido nas últimas
  // 24h") é só leitura — sem zeramento/tracker — então ganha o cartão herói
  // com a tendência embutida. As demais variantes (ritmo, projeção, acumulado
  // zerável) seguem no KPI legado até propagarmos a linguagem.
  const historicalWindow = historicalProductionWindow(namedWidget);
  if (historicalWindow) {
    return (
      <ProductionHeroWidget
        widget={namedWidget}
        value={value}
        rateUnit={sourceRateUnit}
        history={history}
        window={historicalWindow}
        editable={editable}
        onConfigure={handleConfigure}
        onHideChart={handleHideChart}
        onChangeWindow={handleChangeWindow}
      />
    );
  }

  if (namedWidget.widgetType === "production-kpi") {
    return (
      <ProductionKpiWidget
        widget={namedWidget}
        value={value}
        history={history}
        productionTracker={productionTracker}
        onResetProduction={onResetProduction}
        editable={editable}
        onConfigure={handleConfigure}
        onGenerateChart={handleGenerateChart}
      />
    );
  }

  // Gráfico como FACE do widget (nova linguagem). Cada série vira um cartão
  // herói com o número certo e o gráfico embutido:
  //  - linha de RITMO → ritmo ao vivo + estado + linha;
  //  - barras de PRODUÇÃO POR PERÍODO → total + barras (gap = desconhecido).
  // Combos raros (linha/barra de acumulado) seguem no ChartWidget genérico.
  if (namedWidget.widgetType === "line-chart" || namedWidget.widgetType === "bar-chart") {
    const chart = resolveChartConfig(namedWidget);
    if (
      chart.kind === "line" &&
      chart.series === "rate" &&
      (effectiveSignalKind === undefined || effectiveSignalKind === "rate")
    ) {
      return (
        <RateHeroWidget
          widget={namedWidget}
          value={value}
          history={history}
          editable={editable}
          onConfigure={handleConfigure}
        onHideChart={handleHideChart}
          onChangeWindow={handleChangeWindow}
        />
      );
    }
    if (chart.kind === "bar" && chart.series === "throughput") {
      return (
        <ProductionHeroWidget
          widget={namedWidget}
          value={value}
          rateUnit={sourceRateUnit}
          history={history}
          window={chart.window}
          editable={editable}
          onConfigure={handleConfigure}
          onHideChart={handleHideChart}
          onChangeWindow={handleChangeWindow}
        />
      );
    }
  }

  if (namedWidget.widgetType === "line-chart" || namedWidget.widgetType === "bar-chart") {
    return (
      <ChartWidget
        widget={namedWidget}
        value={value}
        history={history}
        editable={editable}
        onConfigure={handleConfigure}
        onHideChart={handleHideChart}
        onChangeWindow={handleChangeWindow}
      />
    );
  }

  // Sinal de estado sempre vira LED (não cai em SilentWidget quando
  // ausente — o LedWidget já mostra "Sem sinal" no apagado).
  if (namedWidget.widgetType === "led") {
    return (
      <LedWidget
        widget={namedWidget}
        value={value}
        editable={editable}
        onConfigure={handleConfigure}
      />
    );
  }

  // Sinais numéricos sem snapshot recente: o cartão herói mostra "--" com o
  // estado de ausência em sentence case (aguardando / sem leitura recente /
  // offline) — o operador entende que a função existe no metaJson mas o
  // gateway não publicou recentemente, sem caixa alta gritando.
  if (!value.isAvailable) {
    return (
      <ValueHeroWidget
        widget={namedWidget}
        value={value}
        editable={editable}
        onConfigure={handleConfigure}
        onGenerateChart={handleGenerateChart}
      />
    );
  }

  if (namedWidget.widgetType === "gauge") {
    return (
      <GaugeWidget
        widget={namedWidget}
        value={value}
        editable={editable}
        onConfigure={handleConfigure}
        onGenerateChart={handleGenerateChart}
      />
    );
  }

  if (namedWidget.widgetType === "progress") {
    return (
      <BarWidget
        widget={namedWidget}
        value={value}
        editable={editable}
        onConfigure={handleConfigure}
      onGenerateChart={handleGenerateChart}
      />
    );
  }

  return (
    <ValueHeroWidget
      widget={namedWidget}
      value={value}
      editable={editable}
      onConfigure={handleConfigure}
      onGenerateChart={handleGenerateChart}
    />
  );
}
